/**
 * כרטיס החברה במשרד — the client's MASTER RECORD. `/tenant/companies/{company_uid}`.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THIS SCREEN EXISTS, GIVEN THAT `/c/{company_uid}` ALREADY DOES
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * A screen was not added to make a route reachable. It was added because two different questions
 * about a company were sharing one address and one of them had no answer at all.
 *
 *   `/c/{company_uid}`              THE WORK SURFACE. Modules, periods, documents. What a bookkeeper
 *                                   opens to do this month's work. It is entered through the context
 *                                   bar and it carries a PERIOD.
 *   `/tenant/companies/{uid}`       THE ADMINISTRATIVE RECORD. The company's legal name, its
 *                                   lifecycle state, its external identifiers, its source bindings,
 *                                   its portfolio. What an office manager opens when a client is
 *                                   renamed, goes dormant, merges, or stops being a client.
 *
 * They are different jobs, held by different capabilities (`companies.company.read` against
 * `companies.company.manage`), reached from different parents, and the second one had NOWHERE TO
 * LIVE: `companies.company.update` and `companies.company.lifecycle` were server routes with no
 * browser path, and `companies.identifier.read` and `companies.portfolio.assign` had none either.
 * Hanging an editing form off the work surface would have put "mark this client as former" one click
 * from "open the trial balance", which is the arrangement that produces the accident.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE LIFECYCLE IS A TRANSITION, NEVER A FIELD
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The server models it as a POST to `/lifecycle` naming a MOVE, deliberately: *"a client cannot
 * express 'set lifecycle_status' at all — it can only ask for a move."* So this screen offers verbs
 * — `העברה לתרדמת`, `סימון כלקוח לשעבר` — and never a dropdown of states. And when a move is refused
 * the server returns where the company actually is plus the moves that WOULD work, which is what
 * turns a refusal into the next three buttons instead of a sentence.
 *
 * `merged_into` is the one move that cannot be expressed without a second company, and the server's
 * input union cannot be constructed without one. The form therefore asks, rather than sending a merge
 * with no target and reading the 400 back.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * AND THE LIFECYCLE STATE IS DISPLAYED, NEVER INFERRED
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * 09 decision 10, measured: `company_tags.json` carried `active=true` for 8 companies, `active=false`
 * for 138, and NO `active` key at all for 167 — the largest group — while two code paths resolved
 * that absence in opposite directions. Every state on this screen is the one the server stated.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { FactList } from '../components/FactList';
import { LifecycleChip } from '../components/LifecycleChip';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { CompanyLifecycleStatus, CompanySummary } from '../api/contract';
import { COMPANY_LIFECYCLE_STATUSES } from '../api/contract';
import { lifecycleStatusHe } from '../api/adminVocabulary';
import {
  companyTransitionRefusalText,
  companyTransitionVerbHe,
  transitionNeedsMergeTarget,
} from '../api/lifecycleText';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { fixtureExternalCompanies, fixturesEnabled } from '../api/fixtures';
import { routes } from '../routes';

export interface TenantCompanyScreenProps {
  readonly companyUid: string;
}

const TITLE_HE = 'כרטיס החברה במשרד';

type Outcome =
  | { readonly kind: 'updated' }
  | { readonly kind: 'moved'; readonly to: CompanyLifecycleStatus }
  | { readonly kind: 'portfolioAssigned' }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

function knownStatuses(values: readonly string[] | undefined): readonly CompanyLifecycleStatus[] {
  const known: readonly string[] = COMPANY_LIFECYCLE_STATUSES;
  return (values ?? []).filter((v): v is CompanyLifecycleStatus => known.includes(v));
}

export function TenantCompanyScreen({ companyUid }: TenantCompanyScreenProps): JSX.Element {
  const ctx = useWorkContext();
  const [legalName, setLegalName] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [entityKind, setEntityKind] = useState('');
  const [taxId, setTaxId] = useState<string | null>(null);
  const [moveTo, setMoveTo] = useState<CompanyLifecycleStatus | null>(null);
  const [mergeTargetUid, setMergeTargetUid] = useState('');
  // THE ROUTE TAKES A PARTNER AND A KIND, NOT A CODE.
  //
  // This screen used to hold a single `portfolioCode` text field and post `{portfolioCode}`.
  // `companies.portfolio.assign` reads `membershipUid` and `kind` and answers 400 to anything else,
  // so the control could never succeed — G-01's shape exactly: the right URL, a body the handler
  // does not read, and a generic refusal that looks like the operation simply failing. The declared
  // request shape in `api/operations.ts` is what turned it into a compile error.
  const [portfolioMembershipUid, setPortfolioMembershipUid] = useState('');
  const [portfolioKind, setPortfolioKind] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="TENANT" actual={ctx.surface} />;
  }

  const company: CompanySummary | null =
    ctx.visibleCompanies.find((c) => c.companyUid === companyUid) ?? null;
  const back = { labelHe: 'חברות במשרד', href: routes.tenantCompanies };

  if (company === null) {
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        {/* Careful not to confirm or deny that a company with this uid exists: that is the gate's rule. */}
        <StateBlock
          titleHe="החברה הזו אינה זמינה"
          bodyHe="לא הצלחנו להציג את החברה שאליה מפנה הקישור."
          tone="unavailable"
          nextStep={
            <a className="btn btn--primary" href={back.href}>
              חזרה לרשימת החברות
            </a>
          }
        />
      </AppShell>
    );
  }

  const bindings = (fixturesEnabled() ? fixtureExternalCompanies : []).filter(
    (e) => e.boundCompanyUid === companyUid,
  );

  async function run(action: () => Promise<Outcome>): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      setOutcome(await action());
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
    }
  }

  /** Renders the company's own conflict vocabulary — and offers the moves the server said would work. */
  function renderCompanyConflict(failure: Extract<ActionFailure, { kind: 'conflict' }>): JSX.Element {
    const text = companyTransitionRefusalText(
      {
        reason: failure.body.error,
        from: knownStatuses(failure.body.from === undefined ? [] : [failure.body.from])[0] ?? null,
        allowedTransitions: knownStatuses(failure.body.allowedTransitions),
      },
      lifecycleStatusHe,
    );
    return (
      <section className="denial" data-tone="blocked" role="alert">
        <h2 className="denial__title">{text.titleHe}</h2>
        <p className="denial__explanation">{text.explanationHe}</p>
        <p className="denial__explanation">{text.unchangedHe}</p>
        <p className="denial__next-step">{text.nextStepHe}</p>
        {/*
          The actionable half made clickable. These are the moves FROM WHERE THE COMPANY ACTUALLY IS,
          which is why they can differ from what the form offered a moment ago — that list was built
          before the refusal, and the refusal is what proved it stale.
        */}
        {text.allowedTransitions.length === 0 ? null : (
          <div className="actions-row">
            {text.allowedTransitions.map((status) => (
              <button
                key={status}
                type="button"
                className="btn"
                onClick={() => setMoveTo(status)}
              >
                {companyTransitionVerbHe(status)}
              </button>
            ))}
          </div>
        )}
      </section>
    );
  }

  function saveDetails(e: FormEvent, current: CompanySummary): void {
    e.preventDefault();
    void run(async () => {
      // The company is in the ADDRESS. `CompanyEditInput` declares `tenantId?: never` and
      // `lifecycleStatus?: never`, so neither can travel with an edit even by accident.
      await callOperation('companies.company.update', {
        params: { company_uid: companyUid },
        body: {
          legalName: legalName ?? current.legalName,
          displayName: displayName ?? current.legalName,
          entityKind,
          taxId: taxId ?? current.registrationNumber,
        },
      });
      return { kind: 'updated' };
    });
  }

  function applyTransition(e: FormEvent): void {
    e.preventDefault();
    const to = moveTo;
    if (to === null) return;
    void run(async () => {
      await callOperation('companies.company.lifecycle', {
        params: { company_uid: companyUid },
        body: transitionNeedsMergeTarget(to)
          ? { to, mergedIntoCompanyUid: mergeTargetUid }
          : { to },
      });
      return { kind: 'moved', to };
    });
  }

  function assignPortfolio(e: FormEvent): void {
    e.preventDefault();
    void run(async () => {
      await callOperation('companies.portfolio.assign', {
        params: { company_uid: companyUid },
        body: { membershipUid: portfolioMembershipUid, kind: portfolioKind },
      });
      return { kind: 'portfolioAssigned' };
    });
  }

  return (
    <AppShell
      titleHe={company.legalName}
      backToParent={back}
      provenanceHe="מצב החברה מוצג כפי שהשרת קבע אותו. הוא אינו נגזר מהיעדר סימון ואינו מנוחש מנתונים אחרים."
    >
      <FactList
        facts={[
          { labelHe: 'השם המלא', value: company.legalName },
          {
            labelHe: 'ח.פ / מספר רישום',
            value: company.registrationNumber === null ? null : (
              <span dir="ltr">{company.registrationNumber}</span>
            ),
            absentHe: 'ללא מספר רישום — זהות החברה אינה שלמה',
          },
          { labelHe: 'מצב החברה', value: <LifecycleChip status={company.lifecycleStatus} /> },
          {
            labelHe: 'קישור למערכת מקור',
            value:
              bindings.length === 0 ? null : (
                <span className="chip-row">
                  {bindings.map((b) => (
                    <span key={b.externalCompanyId} className="chip" dir="ltr">
                      {b.externalCompanyId}
                    </span>
                  ))}
                </span>
              ),
            absentHe: 'אין קישור למערכת מקור — נתונים חיצוניים אינם נקלטים לחברה הזו',
          },
        ]}
      />

      <div className="actions-row">
        <a className="btn btn--primary" href={routes.companyHome(company.companyUid)}>
          פתיחת החברה לעבודה
        </a>
        <a className="btn" href={routes.tenantSources}>
          מקורות נתונים וקישורים
        </a>
        <a className="btn" href={routes.tenantCompanyAccess}>
          הרשאות לחברה הזו
        </a>
      </div>

      {outcome === null ? null : outcome.kind === 'updated' ? (
        <StateBlock
          titleHe="פרטי החברה נשמרו"
          bodyHe="השינוי נרשם ביומן הביקורת. מצב החברה לא השתנה — הוא נקבע בפעולה נפרדת."
        />
      ) : outcome.kind === 'moved' ? (
        <StateBlock
          titleHe="מצב החברה שונה"
          bodyHe={`החברה נמצאת כעת במצב ״${lifecycleStatusHe(outcome.to)}״. השינוי נרשם ביומן יחד עם מי שביצע אותו.`}
        />
      ) : outcome.kind === 'portfolioAssigned' ? (
        <StateBlock
          titleHe="תיק העבודה שויך"
          bodyHe="שיוך תיק עבודה מארגן את החברה בתוך המשרד. הוא אינו מעניק לאיש גישה אליה."
        />
      ) : (
        <ActionFailureNotice failure={outcome.failure} renderConflict={renderCompanyConflict} />
      )}

      <form className="form" onSubmit={(e) => saveDetails(e, company)}>
        <h2 className="form__title">פרטי החברה</h2>
        <p className="form__lead">
          עדכון פרטים אינו משנה את מצב החברה ואינו משנה הרשאות. שינוי השם נשמר בכרטיס אחד, והקישורים
          למערכות המקור ממשיכים לעבוד — הם מפנים לחברה ולא לשם שלה.
        </p>
        <label className="field">
          <span className="field__label">השם המלא</span>
          <input
            className="field__input"
            name="legalName"
            value={legalName ?? company.legalName}
            onChange={(e) => setLegalName(e.currentTarget.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">שם לתצוגה</span>
          <input
            className="field__input"
            name="displayName"
            value={displayName ?? company.legalName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">סוג הישות</span>
          <input
            className="field__input"
            name="entityKind"
            value={entityKind}
            onChange={(e) => setEntityKind(e.currentTarget.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">ח.פ / מספר רישום</span>
          <input
            className="field__input"
            name="taxId"
            dir="ltr"
            value={taxId ?? company.registrationNumber ?? ''}
            onChange={(e) => setTaxId(e.currentTarget.value)}
          />
          <span className="field__hint">
            מספר רישום ריק הוא מצב אמיתי ולא שגיאה. חברה ללא מספר רישום מופיעה ברשימות ומסומנת.
          </span>
        </label>
        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={sending || entityKind === ''}>
            שמירת הפרטים
          </button>
        </div>
      </form>

      <form className="form" onSubmit={applyTransition}>
        <h2 className="form__title">שינוי מצב החברה</h2>
        <p className="form__lead">
          מצב החברה נקבע במעבר מפורש ולא בעריכת שדה. מעבר שאינו אפשרי מהמצב הנוכחי נדחה בשרת, והתשובה
          כוללת את המעברים שכן אפשריים.
        </p>
        <fieldset className="field field--choice">
          <legend className="field__label">המעבר המבוקש</legend>
          {COMPANY_LIFECYCLE_STATUSES.filter((s) => s !== company.lifecycleStatus).map((status) => (
            <label key={status} className="choice">
              <input
                type="radio"
                name="lifecycleTo"
                value={status}
                checked={moveTo === status}
                onChange={() => setMoveTo(status)}
              />
              <span>{companyTransitionVerbHe(status)}</span>
            </label>
          ))}
        </fieldset>

        {moveTo !== null && transitionNeedsMergeTarget(moveTo) ? (
          <fieldset className="field field--choice">
            <legend className="field__label">החברה שאליה מוזגה</legend>
            {/* A merge cannot be expressed without a target, so the form asks rather than failing. */}
            {ctx.visibleCompanies
              .filter((c) => c.companyUid !== companyUid)
              .map((c) => (
                <label key={c.companyUid} className="choice">
                  <input
                    type="radio"
                    name="mergedInto"
                    value={c.companyUid}
                    checked={mergeTargetUid === c.companyUid}
                    onChange={() => setMergeTargetUid(c.companyUid)}
                  />
                  <span>{c.legalName}</span>
                </label>
              ))}
          </fieldset>
        ) : null}

        <div className="actions-row">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={
              sending ||
              moveTo === null ||
              (moveTo !== null && transitionNeedsMergeTarget(moveTo) && mergeTargetUid === '')
            }
          >
            ביצוע המעבר
          </button>
        </div>
        <p className="form__note">
          שינוי מצב אינו מוחק דבר ואינו מסיר הרשאות. הוא משנה את מה שמותר לעשות בחברה מכאן ואילך.
        </p>
      </form>

      <form className="form" onSubmit={assignPortfolio}>
        <h2 className="form__title">תיק עבודה</h2>
        <label className="field">
          <span className="field__label">מזהה השותף האחראי</span>
          <input
            className="field__input"
            name="portfolioMembershipUid"
            dir="ltr"
            value={portfolioMembershipUid}
            onChange={(e) => setPortfolioMembershipUid(e.currentTarget.value)}
          />
          <span className="field__hint">
            תיק עבודה הוא ארגון פנימי של המשרד. הוא אינו מעניק גישה לאיש — גישה לחברה היא הענקה
            מפורשת במסך נפרד.
          </span>
        </label>
        <label className="field">
          <span className="field__label">סוג השיוך</span>
          <input
            className="field__input"
            name="portfolioKind"
            dir="ltr"
            value={portfolioKind}
            onChange={(e) => setPortfolioKind(e.currentTarget.value)}
          />
        </label>
        <div className="actions-row">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={sending || portfolioMembershipUid === '' || portfolioKind === ''}
          >
            שיוך תיק העבודה
          </button>
        </div>
      </form>
    </AppShell>
  );
}
